import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Star, MapPin, Check, AlertTriangle } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import {
  Page,
  Main,
  Card,
  Button,
  Modal,
  Field,
  LoadingState,
  ErrorState,
  Thumb,
  inputClass,
} from '@/components/ui';
import { useApi, useMutation } from '@/hooks/useApi';
import type { Closet, Item } from '@/lib/types';

export function ClosetDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { mutate } = useMutation();

  const { data, loading, error, refetch } = useApi<{ closet: Closet; items: Item[] }>(
    id ? `/closets/${id}` : null,
  );
  const { data: allClosetsRes } = useApi<{ closets: Closet[] }>('/closets');

  const [name, setName] = useState('');
  const [location, setLocation] = useState('');
  const [isMain, setIsMain] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const [showDelete, setShowDelete] = useState(false);
  const [moveTo, setMoveTo] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (data?.closet) {
      setName(data.closet.name);
      setLocation(data.closet.locationText ?? '');
      setIsMain(data.closet.isMain);
    }
  }, [data]);

  const closet = data?.closet;
  const items = data?.items ?? [];
  const itemCount = items.length;
  const otherClosets = (allClosetsRes?.closets ?? []).filter((c) => c.id !== id);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2000);
  };

  const handleSave = async () => {
    if (!id) return;
    setSaving(true);
    const res = await mutate('put', `/closets/${id}`, {
      name: name.trim(),
      locationText: location.trim() || null,
      isMain,
    });
    setSaving(false);
    if (res !== null) {
      showToast('Changes saved');
      refetch();
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    if (itemCount > 0 && !moveTo) return;
    setDeleting(true);
    const path = itemCount > 0 ? `/closets/${id}?move_to=${moveTo}` : `/closets/${id}`;
    const res = await mutate('delete', path);
    setDeleting(false);
    if (res !== null) {
      navigate('/profile/closets');
    }
  };

  return (
    <Page>
      <PageHeader section="Edit Closet" backHref="/profile/closets" backLabel="Closets" />

      <Main className="space-y-6">
        {toast && (
          <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 px-4 py-3 bg-green-600 text-white rounded-xl shadow-lg">
            <Check className="w-4 h-4" />
            <span className="text-sm font-medium">{toast}</span>
          </div>
        )}

        {loading && <LoadingState />}
        {error && <ErrorState message={error} />}

        {!loading && !error && closet && (
          <>
            <Card className="p-4">
              <div className="flex items-center gap-4">
                <div className={`p-4 rounded-xl ${isMain ? 'bg-primary/10' : 'bg-secondary'}`}>
                  <Star className={`w-8 h-8 ${isMain ? 'text-primary' : 'text-muted-foreground'}`} />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-foreground">{name || 'Unnamed Closet'}</h2>
                  <div className="flex items-center gap-1 mt-1">
                    <MapPin className="w-3.5 h-3.5 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">{location || 'No location set'}</p>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">{itemCount} items</p>
                </div>
              </div>
            </Card>

            {/* Edit Form */}
            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide px-1 font-display">
                Closet Details
              </h3>
              <Card className="p-4 space-y-4">
                <Field label="Closet Name">
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Enter closet name"
                    className={inputClass}
                  />
                </Field>
                <Field label="Physical Location">
                  <input
                    type="text"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    placeholder="e.g., Master Bedroom"
                    className={inputClass}
                  />
                </Field>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-foreground">Set as Main Closet</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      "In Closet" will refer to items here
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsMain((v) => !v)}
                    className={`relative w-12 h-7 rounded-full transition-colors ${isMain ? 'bg-primary' : 'bg-border'}`}
                  >
                    <div
                      className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow-sm transition-all ${isMain ? 'left-6' : 'left-1'}`}
                    />
                  </button>
                </div>
              </Card>
            </div>

            <Button fullWidth onClick={handleSave} disabled={saving || !name.trim()}>
              {saving ? 'Saving…' : 'Save Changes'}
            </Button>

            {/* Items */}
            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide px-1 font-display">
                Items ({itemCount})
              </h3>
              {items.length === 0 ? (
                <Card className="p-6 text-center">
                  <p className="text-sm text-muted-foreground">No items in this closet</p>
                </Card>
              ) : (
                <Card className="overflow-hidden divide-y divide-border">
                  {items.map((item) => (
                    <Link
                      key={item.id}
                      to={`/wardrobe/item/${item.id}`}
                      className="flex items-center gap-3 p-3 hover:bg-secondary/50 transition-colors"
                    >
                      <Thumb url={item.primaryPhotoUrl} alt={item.name} className="w-12 h-12 rounded-lg flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{item.name}</p>
                        <p className="text-xs text-muted-foreground truncate">
                          {[item.brand, item.category].filter(Boolean).join(' · ')}
                        </p>
                      </div>
                    </Link>
                  ))}
                </Card>
              )}
            </div>

            {/* Danger Zone */}
            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-destructive uppercase tracking-wide px-1 font-display">Danger Zone</h3>
              <div className="bg-card rounded-2xl border border-destructive/30 p-4">
                <p className="text-sm text-foreground font-medium">Delete Closet</p>
                <p className="text-xs text-muted-foreground mt-1 mb-3">
                  {itemCount > 0
                    ? 'Items in this closet must be moved to another closet. This cannot be undone.'
                    : 'This action cannot be undone.'}
                </p>
                <button
                  onClick={() => {
                    setMoveTo(otherClosets[0]?.id ?? '');
                    setShowDelete(true);
                  }}
                  className="px-4 py-2 border border-destructive text-destructive rounded-xl text-sm font-medium hover:bg-destructive/5 transition-colors"
                >
                  Delete Closet
                </button>
              </div>
            </div>
          </>
        )}
      </Main>

      <Modal
        isOpen={showDelete}
        onClose={() => setShowDelete(false)}
        title="Delete Closet?"
        footer={
          <div className="flex gap-3">
            <Button variant="outline" fullWidth onClick={() => setShowDelete(false)} disabled={deleting}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              fullWidth
              onClick={handleDelete}
              disabled={deleting || (itemCount > 0 && !moveTo)}
            >
              {deleting ? 'Deleting…' : 'Delete'}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <div className="flex items-center justify-center w-12 h-12 bg-destructive/10 rounded-full mx-auto">
            <AlertTriangle className="w-6 h-6 text-destructive" />
          </div>
          <p className="text-sm text-muted-foreground text-center">
            Are you sure you want to delete "{name}"?
          </p>
          {itemCount > 0 && (
            <Field label={`Move ${itemCount} items to`}>
              {otherClosets.length === 0 ? (
                <p className="text-xs text-destructive">
                  No other closet to move items to. Create another closet first.
                </p>
              ) : (
                <select value={moveTo} onChange={(e) => setMoveTo(e.target.value)} className={inputClass}>
                  {otherClosets.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              )}
            </Field>
          )}
        </div>
      </Modal>
    </Page>
  );
}
